/**
 * The report shape for an error an XState invoke caught (PSTUDIO-4194
 * criterion I).
 *
 * Sentry's automatic capture is `globalHandlersIntegration` — `window.onerror`
 * plus `unhandledrejection` — so it only ever sees what nothing caught. Every
 * failure these apps show as a toast IS caught: a rejected invoke goes to
 * `onErrorTarget` + `onErrorActions`, where `recordError` puts it in context and
 * `showToastMessage` prints it, and the error object never leaves that path.
 * Measured on `origin/main` with
 * `git grep -c "type: 'error'" origin/main -- packages/apps/<app>/src`:
 * 49 error toasts in pro-studio and 45 in manager, against ONE call site per
 * app that reaches Sentry (LaunchDarkly init).
 *
 * This module is the pure half — it decides what a report SAYS. The wiring that
 * hands it to `reportError` lives in each app's `xstate.utils.ts`.
 */

/**
 * What every invoke-failure event is named, before the invoke's own id.
 *
 * `es/types.js` declares `ActionTypes.ErrorPlatform = 'error.platform'` and
 * `es/actions.js` builds the event as `` `${ActionTypes.ErrorPlatform}.${id}` ``,
 * where `id` is the invoke's explicit `id` or, by default,
 * `` `${stateNodeId}:invocation[${index}]` `` (`es/utils.js`), with `stateNodeId`
 * itself `[machine.key, ...path].join('.')` (`es/StateNode.js`).
 */
export const INVOKE_ERROR_EVENT_PREFIX = 'error.platform.';

/** What an entrance we cannot read is called, so a tag never goes missing. */
const UNREADABLE_ENTRANCE = 'unknown';

/**
 * Sentry's documented maximum for a tag VALUE.
 *
 * https://docs.sentry.io/platforms/javascript/enriching-events/tags/ (read
 * 2026-09-20): "Tag values have a maximum length of 200 characters and they
 * cannot contain the newline (\n) character". The browser SDK does not enforce
 * it for us — `prepareEvent` applies `maxValueLength` to `request.url` and
 * `exception.value` only — so an over-long value would be left to the server to
 * reject or cut. This module cuts it instead, on the side it chooses.
 */
const MAX_TAG_VALUE_LENGTH = 200;

/** Marks a cut address, so a truncated tag cannot read like a whole one. */
const TRUNCATION_MARK = '...';

/**
 * The shortest invoked-function name worth recording.
 *
 * A minifier renames an imported function to one or two characters. Measured on
 * 2026-09-19 (esbuild --bundle --minify, the default Vite production build; no
 * app here sets `keepNames`): an invoke handed `publishBotVersion` reports
 * `fn.name === 'o'`, while the same bundle without `--minify` reports the real
 * name. A one-letter name in a report is worse than no name — it reads like an
 * answer — so anything this short is dropped rather than carried.
 */
const MIN_MEANINGFUL_NAME_LENGTH = 3;

/**
 * Reads the failing invoke's address out of the error event's own type.
 *
 * This is what names the entrance, and the event is the only input that carries
 * it. The two alternatives were measured on 2026-09-20 against xstate 4.38 with
 * a machine shaped like `prostudio.xstate.ts`:
 *
 * - `meta.state.value` — ABSENT. `Interpreter._exec` passes `{action, state,
 *   _event}` to an inline action only while `predictableActionArguments` is
 *   falsy, and all six machines in the two apps set it `true`, so the `state`
 *   key does not exist. With it forced `false` the value reads `"idle"` — the
 *   state the machine transitioned TO, not the one the invoke failed in — so
 *   that input is wrong even where it is present.
 * - `src.name` — renamed to one character by the production build (above).
 *
 * The event type is built from the machine config's own keys and string
 * literals, which a minifier leaves alone, and it is identical in both modes:
 * `error.platform.pro-studio.checkingUserInfo.projectFlow.botDataLoading:invocation[0]`.
 */
export const parseInvokeEntrance = (eventType?: string): string => {
  if (typeof eventType !== 'string') {
    return UNREADABLE_ENTRANCE;
  }

  if (!eventType.startsWith(INVOKE_ERROR_EVENT_PREFIX)) {
    return UNREADABLE_ENTRANCE;
  }

  // A tag value cannot hold a newline, and an address is a single line by
  // construction — so any whitespace here is damage, and folding it keeps the
  // value acceptable instead of letting Sentry refuse the whole tag.
  const entrance = eventType
    .slice(INVOKE_ERROR_EVENT_PREFIX.length)
    .replace(/\s+/g, ' ')
    .trim();

  if (!entrance) {
    return UNREADABLE_ENTRANCE;
  }

  if (entrance.length <= MAX_TAG_VALUE_LENGTH) {
    return entrance;
  }

  // Keep the TAIL: the state path and the invoke index are what identify this
  // entrance, while the head is the machine id, which the `app` tag already
  // answers for.
  return (
    TRUNCATION_MARK +
    entrance.slice(
      entrance.length - (MAX_TAG_VALUE_LENGTH - TRUNCATION_MARK.length)
    )
  );
};

export interface InvokeErrorReportInput {
  /**
   * The rejected invoke's own event type — the entrance's address.
   *
   * `error.platform.<invoke id>`; see `parseInvokeEntrance`.
   */
  eventType?: string;
  /** Which app raised it, so one Sentry project can tell the two apart. */
  app: string;
  /** `src.name`, when the build has not renamed it away. */
  invokeName?: string;
}

export interface InvokeErrorReport {
  source: string;
  type: string;
  tags: Record<string, string>;
  extra?: Record<string, unknown>;
}

/**
 * Builds what a caught invoke failure reports.
 *
 * There is deliberately NO exception list on this path, and that is a measured
 * decision rather than an omission. Criterion I asks for expected user refusals
 * to be kept out of a paid per-event product, and names two — a duplicate node
 * number and an unpublishable draft. Neither reaches an invoke: both are
 * decided by the `canPublish` computed and the pre-publish guard in
 * `PublishModal.onPublish`, which disable the button and raise a toast without
 * any promise rejecting. Every one of the 45 invokes in `prostudio.xstate.ts`
 * and the 4 in each `user.xstate.ts` is a network operation (`load*`,
 * `create*`, `update*`, `delete*`, `publish*`), whose failure is a real defect.
 *
 * So the list is empty because nothing qualifies for it today. When an invoke
 * does start refusing something the user is expected to hit, the opt-out
 * belongs here — as a named case with its reason, not as a blanket flag.
 */
export const buildInvokeErrorReport = ({
  eventType,
  app,
  invokeName,
}: InvokeErrorReportInput): InvokeErrorReport => {
  const usableName =
    invokeName && invokeName.length >= MIN_MEANINGFUL_NAME_LENGTH
      ? invokeName
      : undefined;

  return {
    source: 'xstate-invoke',
    type: 'invoke',
    tags: {
      invokeState: parseInvokeEntrance(eventType),
      app,
    },
    ...(usableName ? { extra: { invokeName: usableName } } : {}),
  };
};
